import React, { useEffect, useState } from 'react';

import { Platform, StyleSheet } from 'react-native';

import { Box } from './box';

type AccessibilityLiveRegion = 'none' | 'polite' | 'assertive';

const mapLiveRegionToAriaLive = (
  liveRegion: AccessibilityLiveRegion
): 'polite' | 'assertive' | 'off' => {
  if (liveRegion === 'none') {
    return 'off';
  }
  return liveRegion;
};

/**
 * Preserves the existing hidden page-title view and live-region attributes.
 * Successful screen-reader announcements are not guaranteed by this combination
 * of hidden attributes; behavior corrections require separate verification.
 */
export const PageAnnouncer: React.FC<React.PropsWithChildren<{
  pageTitle: string;
  accessibilityLiveRegion?: AccessibilityLiveRegion;
}>> = ({ pageTitle, accessibilityLiveRegion = 'polite' }) => {
  const [currentPage, setCurrentPage] = useState('');

  useEffect(() => {
    setCurrentPage(pageTitle);
  }, [pageTitle]);

  return (
    <Box
      accessible={false}
      accessibilityLabel={currentPage}
      testID="page-announcer"
      accessibilityLiveRegion={accessibilityLiveRegion}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      aria-hidden={true}
      {...(Platform.OS === 'web' && {
        'aria-live': mapLiveRegionToAriaLive(accessibilityLiveRegion),
        role: 'status',
      })}
      style={styles.hidden}
    />
  );
};

const styles = StyleSheet.create({ hidden: { width: 0, height: 0, opacity: 0 } });
